import { ExternalLink } from "@/components/ExternalLink";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";

export type G4Position = {
  id: number;
  strategy_from_id?: number | null;
  strategy_from_name?: string | null;
  strategy_to_id?: number | null;
  strategy_to_name?: string | null;
  quantity?: number | null;
  date?: string | null;
  weight?: number | null;
  market_value_local_ccy?: number | null;
  market_value_ptf_ccy?: number | null;
  g4_time_serie_file_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const tableName = "g4_positions";
const t = (key: string, defaultValue: string) =>
  i18n.t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });

export const g4_positions_columns: ColumnDef<G4Position>[] = [
  {
    accessorKey: "id",
    header: t("id", "ID"),
  },
  {
    accessorKey: "strategy_from_name",
    header: t("strategy_from_name", "Portefeuille"),
    cell: ({ row }) => {
      const id = row.original.strategy_from_id;
      const name = row.original.strategy_from_name;
      if (!id || !name) return "";
      return (
        <ExternalLink
          href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
          className="text-xs"
          underline="hover"
        >
          {name}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "strategy_to_name",
    header: t("strategy_to_name", "Stratégie"),
    cell: ({ row }) => {
      const id = row.original.strategy_to_id;
      const name = row.original.strategy_to_name;
      if (!id || !name) return "";
      return (
        <ExternalLink
          href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
          className="text-xs"
          underline="hover"
        >
          {name}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "date",
    header: t("date", "Date"),
    cell: ({ row }) => {
      const value = row.getValue("date");
      return value ? parseDate(String(value)) : "";
    },
  },
  {
    accessorKey: "quantity",
    header: () => <div className="text-right">{t("quantity", "Quantity")}</div>,
    cell: ({ row }) => (
      <div className="text-right">{formatNumber(row.getValue("quantity"))}</div>
    ),
  },
  {
    accessorKey: "weight",
    header: () => <div className="text-right">{t("weight", "Weight")}</div>,
    cell: ({ row }) => (
      <div className="text-right">{formatNumber(row.getValue("weight"))}</div>
    ),
  },
  {
    accessorKey: "market_value_local_ccy",
    header: () => (
      <div className="text-right">
        {t("market_value_local_ccy", "Market value (local currency)")}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue("market_value_local_ccy"))}
      </div>
    ),
  },
  {
    accessorKey: "market_value_ptf_ccy",
    header: () => (
      <div className="text-right">
        {t("market_value_ptf_ccy", "Market value (portfolio currency)")}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue("market_value_ptf_ccy"))}
      </div>
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
